/**
 * 军师「交接卡」action 解析(ADV-HANDOFF-PC-1,2026-08-18 P1)——**两端共享单一源**。
 *
 * ★病因就是「两端各做一半」:波一只在 wecom-h5 实现了交接卡,PC console 的 chatState
 * 只解析 style_gallery、其余 action **静默丢弃** —— ai 侧 handoff_to_editor 工具真调了、
 * ui_action 真发了(trace 5b5e3523…),军师嘴上说「给你交接卡」,PC 上什么也没有。
 * 所以这次不在 PC 抄第二份,把 H5 那份提到 shared,两端同一个解析器、同一套校验。
 *
 * ★校验必须严(纵深防御):route 缺失或不是**站内相对路径**就整条丢掉。放行外站地址
 * 等于让模型能把用户带去任意站点;后端只从目录取不是免检理由 —— 目录哪天被写坏,这里还兜得住。
 * `//evil.com` 是协议相对地址(会跳出站),必须跟着 `/` 一起挡。
 */

export interface EditorHandoff {
  capabilityId: string;
  title: string;
  route: string;
  agentMode: "REDIRECT" | "GUIDE";
  prefillPrompt?: string;
  prefillNote?: string;
  /**
   * ai 侧派生好的场景预设,**原样透传给 `parseScenePreset`**;能力对不上时 ai 不发这个 key。
   *
   * ★为什么不再由 console 自拼(0819 真浏览器实测):此前这里塞的是
   * `{ kind: capabilityId }`,而 `parseScenePreset` 是**精确匹配**、认的是**页面级 kind**
   * (`longimage` 而非 `longimage_goods`)且 `longimage` 还必须带 `usage`
   * ⇒ 整包被丢弃返回 null,**十条长图能力全都对不上**。
   * 这不是跨仓不一致,是 **console 一边之内自相矛盾**:自己塞进去的值,自己的解析器不认。
   *
   * ★职责划分:「这是一张什么用途的长图」是**能力目录的知识**(ai 仓 registry.yaml),
   * 「场景预设长什么形状」是 console 的知识。所以由 ai 按目录派生、console 只负责验型。
   * 对面真源码:`app/modules/advisor/tools.py::_scene_preset_for()`(对不上返回 None,不猜)。
   */
  scenePreset?: Record<string, unknown>;
}

/** actions 里全部合法 editor_handoff(按 capabilityId 去重、保序);一条都没有 → undefined。 */
export function parseHandoffs(actions: unknown[] | undefined): EditorHandoff[] | undefined {
  const out: EditorHandoff[] = [];
  const seen = new Set<string>();
  for (const a of actions ?? []) {
    if (!a || typeof a !== "object") continue;
    const o = a as Record<string, unknown>;
    if (o.type !== "editor_handoff") continue;
    const route = typeof o.route === "string" ? o.route : "";
    const capabilityId = typeof o.capabilityId === "string" ? o.capabilityId : "";
    const title = typeof o.title === "string" ? o.title : "";
    const mode = o.agentMode === "GUIDE" ? "GUIDE" : "REDIRECT";
    // 站内相对路径:必须以单个 / 开头(`//evil.com` 是协议相对地址,会跳出站)
    const inSite = route.startsWith("/") && !route.startsWith("//");
    if (!inSite || !capabilityId || !title || seen.has(capabilityId)) continue;
    seen.add(capabilityId);
    out.push({
      capabilityId,
      title,
      route,
      agentMode: mode,
      ...(typeof o.prefillPrompt === "string" && o.prefillPrompt
        ? { prefillPrompt: o.prefillPrompt }
        : {}),
      ...(typeof o.prefillNote === "string" && o.prefillNote ? { prefillNote: o.prefillNote } : {}),
      // ai 派生好的场景预设:**只收下、不验型**(验型是 parseScenePreset 的职责,
      // 它对每种 kind 有各自的必填项;这里再验一遍会变成同一约束存在于两个地方)。
      ...(o.scenePreset && typeof o.scenePreset === "object"
        ? { scenePreset: o.scenePreset as Record<string, unknown> }
        : {}),
    });
  }
  return out.length > 0 ? out : undefined;
}

/**
 * 交接卡 → `navigate(route, { state })` 的 state。**只带路不代做**(REDIRECT 红线):
 * 军师把料收齐送到编辑页门口,活儿仍由用户在那页自己下单。
 *
 * ★走 **router state** 而不是 query:各创建页消费的既有契约就是
 * `parseScenePreset(location.state)`(H5 波一起就是这套)。两端共用本函数 = 字段语义不会漂。
 */
export function handoffNavState(h: EditorHandoff): Record<string, unknown> {
  return {
    // 既有契约:各创建页 parseScenePreset(location.state) 消费。
    // ★原样透传 ai 派生的形状;**ai 没发就不带** —— 不自己造一个会被验型器丢掉的
    //   (那 12 条能力的编辑页本来就不吃预设,硬塞等于制造一个静默失败)。
    ...(h.scenePreset ? { scenePreset: h.scenePreset } : {}),
    ...(h.prefillPrompt ? { prefillPrompt: h.prefillPrompt } : {}),
    ...(h.prefillNote ? { prefillNote: h.prefillNote } : {}),
    // 来源标记:便于埋点区分「军师带过来的」与「用户自己点卡进来的」
    from: "advisor_handoff",
  };
}

/** 卡面副标:GUIDE=这步得本人来,REDIRECT=料已备好(两端同一句,别各写各的)。 */
export function handoffHint(h: EditorHandoff): string {
  return h.agentMode === "GUIDE" ? "这步得你本人来 · 点我过去" : "料已备好 · 点我带过去调";
}
